/**
 * Günsonu PDF için veri özetleri + profesyonel kısa rapor.
 */
import {
  BRANCH_LABELS,
  BRANCH_COMPARE_ORDER,
  KASA_CATEGORY_LABELS,
  formatMoneyTr,
  formatQtyTr,
  partyName,
} from './gunsonuWhatsappReport';
import { summarizeCategorySales } from './gunsonuCategorySales';

function dayBounds(base = new Date()) {
  const start = new Date(base);
  start.setHours(0, 0, 0, 0);
  const end = new Date(base);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

export function filterIslemlerByDay(islemler, dayDate) {
  const { start, end } = dayBounds(dayDate);
  return (islemler || []).filter((i) => {
    const t = i.tarih?.getTime?.() || i.timestamp?.toDate?.()?.getTime?.() || 0;
    return t >= start.getTime() && t <= end.getTime();
  });
}

/** @deprecated PDF artık fetchFirmaBakiyeleri kullanır; geriye uyum. */
export function buildFirmaBakiyeRows(islemler, limit = 50) {
  const firmaMap = {};
  (islemler || []).forEach((data) => {
    const firmaIsmi = data?.firma?.firmaIsmi?.trim?.();
    if (!firmaIsmi) return;
    if (!firmaMap[firmaIsmi]) firmaMap[firmaIsmi] = { toplamHas: 0 };
    const hasEsdeger = Number(data.hasEsdeger) || 0;
    if (hasEsdeger <= 0) return;
    const tur = data.islemTuru || '';
    if (['Giriş', 'Alış', 'İade', 'Değişim'].includes(tur)) {
      firmaMap[firmaIsmi].toplamHas += hasEsdeger;
    } else if (['Çıkış', 'Satış'].includes(tur)) {
      firmaMap[firmaIsmi].toplamHas -= hasEsdeger;
    }
  });

  return Object.entries(firmaMap)
    .map(([firmaIsmi, v]) => ({
      firmaIsmi,
      toplamHas: Number(v.toplamHas.toFixed(2)),
    }))
    .sort((a, b) => a.firmaIsmi.localeCompare(b.firmaIsmi, 'tr'))
    .slice(0, limit);
}

export function buildKasaPanelRows(kasaData, limit = 16) {
  const rows = [];
  Object.entries(KASA_CATEGORY_LABELS).forEach(([catKey, cat]) => {
    const catData = kasaData?.[catKey] || {};
    Object.entries(cat.items).forEach(([itemKey, label]) => {
      const raw = catData?.[itemKey]?.[cat.field];
      const val = Number(raw) || 0;
      if (Math.abs(val) < 0.0001) return;
      const decimals = cat.field === 'gram' ? 2 : catKey === 'doviz' ? 2 : 0;
      rows.push({
        category: cat.name,
        label,
        value: formatQtyTr(val, decimals),
        unit: cat.unit || '',
        sortVal: Math.abs(val),
      });
    });
  });
  return rows.sort((a, b) => b.sortVal - a.sortVal).slice(0, limit);
}

export function buildMagazaDurumRows(kasaData, firmaRows) {
  const rows = [];
  Object.entries(KASA_CATEGORY_LABELS).forEach(([catKey, cat]) => {
    const catData = kasaData?.[catKey] || {};
    let filled = 0;
    let sum = 0;
    Object.entries(cat.items).forEach(([itemKey]) => {
      const raw = catData?.[itemKey]?.[cat.field];
      const val = Number(raw) || 0;
      if (Math.abs(val) >= 0.0001) {
        filled += 1;
        sum += val;
      }
    });
    const decimals = cat.field === 'gram' || catKey === 'doviz' ? 2 : 0;
    const unit = cat.unit ? ` ${cat.unit}` : '';
    rows.push({
      label: cat.name,
      detail:
        filled > 0
          ? `${filled} kalem · Σ ${formatQtyTr(sum, decimals)}${unit}`
          : 'Boş',
    });
  });

  const debt = (firmaRows || []).reduce((s, r) => s + (Number(r.toplamHas) || 0), 0);
  rows.push({
    label: 'Firma borcu (has)',
    detail: `${formatQtyTr(debt, 2)} g`,
  });

  return { categoryRows: rows, debt, bankNet: 0 };
}

function formatIslemSaat(islem) {
  const d =
    islem?.tarih instanceof Date
      ? islem.tarih
      : islem?.timestamp?.toDate?.() || null;
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

export function buildIslemPanelRows(islemler, limit = 250) {
  const sorted = [...(islemler || [])].sort((a, b) => {
    const ta = a.tarih?.getTime?.() || 0;
    const tb = b.tarih?.getTime?.() || 0;
    return ta - tb;
  });
  return sorted.slice(0, limit).map((islem) => {
    const adet = Number(islem.adet) || 0;
    const gram = Number(islem.gram) || 0;
    const qty = [
      adet ? `${formatQtyTr(adet)} ad` : '',
      gram ? `${formatQtyTr(gram, 2)} g` : '',
    ]
      .filter(Boolean)
      .join(' ');
    const pay = [];
    const nakit = Number(islem.nakit) || 0;
    const havale = Number(islem.havaleEft) || 0;
    const kart = Number(islem.krediKarti) || 0;
    if (nakit) pay.push(`N:${formatMoneyTr(nakit)}`);
    if (havale) pay.push(`H:${formatMoneyTr(havale)}`);
    if (kart) pay.push(`K:${formatMoneyTr(kart)}`);
    return {
      saat: formatIslemSaat(islem),
      tur: islem.islemTuru || '—',
      kim: partyName(islem),
      urun: islem.urun || islem.kategori || '—',
      qty: qty || '—',
      tutar: Number(islem.toplamTutar) ? formatMoneyTr(islem.toplamTutar) : '—',
      odeme: pay.join(' ') || '—',
    };
  });
}

function summarizePayments(islemler) {
  let nakit = 0;
  let havale = 0;
  let kart = 0;
  let bakiye = 0;
  let toplam = 0;
  const byTur = {};
  (islemler || []).forEach((i) => {
    const tur = i.islemTuru || 'Diğer';
    byTur[tur] = (byTur[tur] || 0) + 1;
    nakit += Number(i.nakit) || 0;
    havale += Number(i.havaleEft) || 0;
    kart += Number(i.krediKarti) || 0;
    bakiye += Number(i.bakiye) || 0;
    toplam += Number(i.toplamTutar) || 0;
  });
  return { nakit, havale, kart, bakiye, toplam, byTur, count: (islemler || []).length };
}

export function formatSoldQty(product) {
  const adet = Number(product?.adet) || 0;
  const gram = Number(product?.gram) || 0;
  const parts = [];
  if (adet > 0.0001) parts.push(`${formatQtyTr(adet)} adet`);
  if (gram > 0.0001) parts.push(`${formatQtyTr(gram, 2)} g`);
  if (!parts.length && Number(product?.count) > 0) {
    parts.push(`${product.count} işlem`);
  }
  return parts.join('  ·  ') || '—';
}

/** Satış işlemlerinden en çok satılan ürünler (adet / gram). */
export function topSoldProducts(islemler, limit = 5) {
  const map = {};
  (islemler || []).forEach((i) => {
    if (String(i.islemTuru || '') !== 'Satış') return;
    const name = i.urun || i.kategori || 'Diğer';
    if (!map[name]) map[name] = { name, adet: 0, gram: 0, tutar: 0, count: 0 };
    map[name].adet += Number(i.adet) || 0;
    map[name].gram += Number(i.gram) || 0;
    map[name].tutar += Number(i.toplamTutar) || 0;
    map[name].count += 1;
  });
  return Object.values(map)
    .sort(
      (a, b) =>
        b.tutar - a.tutar || b.adet - a.adet || b.gram - a.gram || b.count - a.count
    )
    .slice(0, limit)
    .map((p) => ({ ...p, qtyLabel: formatSoldQty(p) }));
}

export function summarizeBranchDay(islemler) {
  const pay = summarizePayments(islemler);
  let satisCount = 0;
  (islemler || []).forEach((i) => {
    if (String(i.islemTuru || '') === 'Satış') satisCount += 1;
  });
  return {
    islemCount: pay.count,
    satisCount,
    ciro: pay.toplam,
    nakit: pay.nakit,
    havale: pay.havale,
    kart: pay.kart,
    ...summarizeCategorySales(islemler),
  };
}

export function normalizeBranchCompare(list, currentKey, fallbackIslemler) {
  const byKey = {};
  (list || []).forEach((row) => {
    if (row?.key) byKey[row.key] = row;
  });
  if (currentKey && !byKey[currentKey]) {
    byKey[currentKey] = {
      key: currentKey,
      ...summarizeBranchDay(fallbackIslemler),
    };
  }
  return BRANCH_COMPARE_ORDER.map((meta) => {
    const row = byKey[meta.key] || {};
    return {
      key: meta.key,
      label: meta.label,
      shortLabel: meta.shortLabel,
      isCurrent: meta.key === currentKey,
      islemCount: Number(row.islemCount) || 0,
      satisCount: Number(row.satisCount) || 0,
      ciro: Number(row.ciro) || 0,
      nakit: Number(row.nakit) || 0,
      havale: Number(row.havale) || 0,
      kart: Number(row.kart) || 0,
      takiGram: Number(row.takiGram) || 0,
      gramAltinGram: Number(row.gramAltinGram) || 0,
      sarrafiyeAdet: Number(row.sarrafiyeAdet) || 0,
    };
  });
}

/**
 * Kısa rapor — başlık + satır satır etiket/değer (PDF’de okunaklı).
 */
export function buildGunsonuInsightBlocks({
  todayIslemler,
  lastWeekIslemler,
}) {
  const today = summarizePayments(todayIslemler);
  const week = summarizePayments(lastWeekIslemler);
  const sold = topSoldProducts(todayIslemler, 5);
  const blocks = [];

  const turLine = Object.entries(today.byTur)
    .sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0]), 'tr'))
    .map(([k, v]) => `${k} ${v}`)
    .join('  ·  ');

  if (today.count === 0) {
    blocks.push({
      title: 'İşlem',
      rows: [
        ['Bugün', 'Kayıtlı işlem yok'],
        ['Geçen hafta', `${week.count} işlem`],
      ],
    });
  } else {
    const rows = [['Adet', String(today.count)]];
    if (turLine) rows.push(['Dağılım', turLine]);
    blocks.push({ title: 'İşlem', rows });
  }

  if (today.toplam > 0 || today.nakit || today.havale || today.kart) {
    blocks.push({
      title: 'Tutar',
      rows: [['Gün tutarı', formatMoneyTr(today.toplam)]],
    });
  }

  const payRows = [];
  if (today.nakit) payRows.push(['Nakit', formatMoneyTr(today.nakit)]);
  if (today.havale) payRows.push(['Havale / EFT', formatMoneyTr(today.havale)]);
  if (today.kart) payRows.push(['Kredi kartı', formatMoneyTr(today.kart)]);
  if (today.bakiye) payRows.push(['Bakiye', formatMoneyTr(today.bakiye)]);
  if (payRows.length) {
    blocks.push({ title: 'Tahsilat', rows: payRows });
  }

  if (sold.length) {
    blocks.push({
      title: 'En çok satılan 5 ürün',
      rows: sold.map((p) => [p.name, p.qtyLabel]),
    });
  } else {
    blocks.push({
      title: 'En çok satılan 5 ürün',
      rows: [['Bugün', 'Satış yok']],
    });
  }

  if (week.count > 0) {
    const diff = today.count - week.count;
    let islemCmp = `aynı  (${today.count})`;
    if (diff > 0) islemCmp = `+${diff}  (${week.count} → ${today.count})`;
    if (diff < 0) islemCmp = `−${Math.abs(diff)}  (${week.count} → ${today.count})`;
    const rows = [['İşlem adedi', islemCmp]];
    if (week.toplam > 0 || today.toplam > 0) {
      const tDiff = today.toplam - week.toplam;
      if (Math.abs(tDiff) >= 1) {
        rows.push([
          'Ciro',
          tDiff > 0
            ? `+${formatMoneyTr(tDiff)}`
            : `−${formatMoneyTr(Math.abs(tDiff))}`,
        ]);
      } else {
        rows.push(['Ciro', 'aynı seviyede']);
      }
    }
    blocks.push({ title: 'Geçen haftaya göre', rows });
  }

  return blocks;
}

/** Düz metin (satır satır); eski çağrılar için. */
export function buildGunsonuInsightText(args) {
  return buildGunsonuInsightBlocks(args)
    .map((block) => {
      const body = (block.rows || [])
        .map(([label, value]) => `${label}: ${value}`)
        .join('\n');
      return `${block.title}\n${body}`;
    })
    .join('\n\n');
}

export function buildGunsonuDashboardPayload({
  subeKey,
  dateLabel,
  kasaData,
  allIslemler,
  todayIslemler,
  lastWeekIslemler,
  magazaRows,
  firmaRows,
  firmaGenelToplam,
  branchCompare,
}) {
  const resolvedFirmaRows =
    Array.isArray(firmaRows) && firmaRows.length
      ? firmaRows
      : buildFirmaBakiyeRows(allIslemler, 50);

  const kasaRows = buildKasaPanelRows(kasaData, 40);
  const islemRows = buildIslemPanelRows(todayIslemler, 250);
  const magaza =
    Array.isArray(magazaRows) && magazaRows.length
      ? magazaRows
      : buildMagazaDurumRows(kasaData, resolvedFirmaRows).categoryRows;

  const genel =
    typeof firmaGenelToplam === 'number'
      ? firmaGenelToplam
      : resolvedFirmaRows.reduce((s, r) => s + (Number(r.toplamHas) || 0), 0);

  const insightArgs = {
    todayIslemler,
    lastWeekIslemler,
  };
  const insightBlocks = buildGunsonuInsightBlocks(insightArgs);
  const insight = buildGunsonuInsightText(insightArgs);

  return {
    subeKey: subeKey || '',
    branchLabel: BRANCH_LABELS[subeKey] || String(subeKey || ''),
    dateLabel: dateLabel || '',
    kasaRows,
    islemRows,
    firmaRows: resolvedFirmaRows,
    firmaGenelToplam: parseFloat(Number(genel).toFixed(2)),
    magazaRows: magaza,
    insight,
    insightBlocks,
    branchCompare: normalizeBranchCompare(branchCompare, subeKey, todayIslemler),
    meta: {
      todayCount: (todayIslemler || []).length,
      lastWeekCount: (lastWeekIslemler || []).length,
      firmaCount: resolvedFirmaRows.length,
    },
  };
}
